import {
  AdditionalVisualDescription,
  ContainsDissolvedSalt,
  ExcludedMaterial,
} from 'Components/Worksheets/Core/Fields';
import { valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { pipe } from 'Lib/Hooks/Selectors';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, ButtonGroup, Checkbox, Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from '../../../../Layout';
import { IWorksheetComponent, WorksheetBalanceFieldSet, WorksheetEquipmentDropdown } from '../../../Core';
import { GrainSize, MethodType } from '../../types';

let BsEnIsoPreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const [testConditionsEditable, setTestConditionsEditable] = useState(false);

  const testMethodOptions = [
    { label: 'BS 1377-2 Clause 4.1', value: MethodType.BS_1377_2 },
    { label: 'BS EN ISO 17892-1', value: MethodType.BS_EN_ISO },
  ];

  const visualNominalParticleSizeSieveOptions = useSelector(
    pipe(createWorksheetSelector!('Preparation.AllVisualNominalParticleSizeSieves'), (diameters) =>
      (diameters || []).map((diameter: any) => ({
        value: diameter.Id,
        label: diameter.Caption,
        disabled: diameter.Disabled,
      }))
    )
  );

  const hideGrainSize = useSelector(pipe(createWorksheetSelector!('Preparation.HideGrainSize'), valueOrDefault(false)));

  const grainSizeOptions: Array<{ label: string; value: GrainSize }> = [
    { label: 'Fine', value: GrainSize.Fine },
    { label: 'Coarse', value: GrainSize.Coarse },
  ];

  const excludeMaterialReadOnly = useSelector(
    pipe(createWorksheetSelector!('Preparation.ExcludedMaterialReadOnly'), valueOrDefault(false))
  );

  return (
    <>
      {/* Editable fields */}
      <ListFieldSection visible={testConditionsEditable}>
        <Field input={<RadioGroup />} name="Preparation.Method" options={testMethodOptions} />
        <Field input={<NumericInput />} name="Equipment.DryingTemperature" />
        <Field input={<Checkbox />} name="Preparation.ContainsGypsum" />
        <AdditionalVisualDescription editable appendNamePrefix="Preparation" />
        <ExcludedMaterial editable={!excludeMaterialReadOnly} appendNamePrefix="Preparation" />
        <ContainsDissolvedSalt editable visible={testConditionsEditable} appendNamePrefix="Preparation" />
      </ListFieldSection>

      {/* Read-only fields */}
      <>
        <ListFieldSection visible={!testConditionsEditable}>
          <Field input={<TextOutput />} name="Preparation.Method" />
          <Field input={<TextOutput />} name="Equipment.DryingTemperature" />
          <Field input={<TextOutput />} name="Preparation.ContainsGypsum" format={{ type: FormatTypes.YesNo }} />
          <AdditionalVisualDescription appendNamePrefix="Preparation" />
          <ExcludedMaterial appendNamePrefix="Preparation" />
          <ContainsDissolvedSalt visible={!testConditionsEditable} appendNamePrefix="Preparation" />
        </ListFieldSection>
        <ListFieldSection visible={!testConditionsEditable}>
          <FieldSet>
            <ButtonGroup>
              <Button
                onClick={() => {
                  setTestConditionsEditable(true);
                }}
              >
                Change
              </Button>
            </ButtonGroup>
          </FieldSet>
        </ListFieldSection>
      </>

      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="Preparation.VisualNominalParticleSize"
          options={visualNominalParticleSizeSieveOptions}
          simpleValue
        />
        <Field
          input={<RadioGroup />}
          name="Preparation.GrainSize"
          options={grainSizeOptions}
          visible={!hideGrainSize}
        />
        <WorksheetBalanceFieldSet name="Equipment.Balance" />
        <FieldSet>
          <Field input={<WorksheetEquipmentDropdown />} equipmentType="oven" name="Equipment.Oven" />
        </FieldSet>
      </ListFieldSection>
    </>
  );
};

BsEnIsoPreparationSection = withInjectedProps(BsEnIsoPreparationSection);

export { BsEnIsoPreparationSection };
export default BsEnIsoPreparationSection;
